import { ChatBubbleBottomCenterTextIcon, SparklesIcon, UserIcon } from "@heroicons/react/24/solid";
import { Persona } from "@moe/priv/model/persona";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PersonaSelectorModal } from "@web/route-services/chat/PersonaSelector";
import { useAppContext } from "@web/route-services/root/AppContext";

import { EllipsisHorizontalCircleIcon } from "@heroicons/react/24/solid";
import { SheetClose } from "@moe/priv/components/ui/sheet";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem
} from "@moe/priv/components/ui/sidebar";
import { toTitleCase } from "@moe/priv/utils";
import { useAPISettings } from "@web/hooks/use-api-settings";
import { APISettingsModal } from "@web/route-services/chat/settings/APISettingsModal";
import { CosmeticSettingsModal } from "@web/route-services/chat/settings/CosmeticSettingsModal";
import { ManageCharactersModal } from "@web/route-services/chat/settings/ManageCharactersModal";
import { MemoryBoxModal } from "@web/route-services/chat/settings/MemoryBoxModal";
import { ResponseSettingsModal } from "@web/route-services/chat/settings/ResponseSettingsModal";
import { useChatData } from "@web/route-services/chat/use-chat-data";
import React, { Fragment } from "react";

interface Item {
  label: string;
  value: string | undefined;
  onClick?: (e: React.MouseEvent) => void;
  closeOnClick?: boolean;
}

interface SidebarGroupSpec {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  onClick?: () => void;
  items: Item[];
}

interface Props extends React.ComponentProps<typeof Sidebar> {
  chatID: number;
}

/**
 * The chat settings sidebar that is rendered when the ... button in the chat input is clicked.
 */
export function SettingsSidebar({ chatID, ...rest }: Props) {
  const { access, createModal } = useAppContext();
  const queryClient = useQueryClient();
  const apiSettings = useAPISettings();
  const { data: chat } = useChatData(chatID);
  const chatSettings = chat?.settings;

  const { data: persona, isLoading: personaLoading } = useQuery({
    queryKey: ["persona", chatID],
    queryFn: async () => {
      if (!chatID) return;
      return access.persona.getByChatID(chatID);
    }
  });

  const openPersonaSelector = async () => {
    if (!chatID) throw new Error("Chat ID is required to select a persona");
    const selectHandler = async (persona: Persona) => {
      await access.chat.update({ id: chatID, personaID: persona.id });
      queryClient.invalidateQueries({ queryKey: ["chatPersona", chatID] });
    };
    createModal({ content: <PersonaSelectorModal onSelect={selectHandler} /> });
  };

  /**
   * The configuration object that defines the structure of the sidebar
   */
  const sidebarSpec: SidebarGroupSpec[] = [
    {
      icon: UserIcon,
      title: "My Persona",
      onClick: openPersonaSelector,
      items: [
        {
          label: "Name:",
          value: persona?.name ?? "Select a persona"
        },
        {
          label: "Description:",
          value: persona?.description ?? "Select a persona"
        }
      ]
    },
    {
      icon: ChatBubbleBottomCenterTextIcon,
      title: "Response Settings",
      onClick: () => {
        createModal({
          content: <ResponseSettingsModal chatID={chatID} />
        });
      },
      items: [
        {
          label: "Memory:",
          value: chat?.memory ?? "___",
          onClick: (e) => {
            e.stopPropagation();
            createModal({
              content: <MemoryBoxModal chatID={chatID} />
            });
          }
        },
        { label: "Custom Instructions:", value: chatSettings?.customInstructions ?? "___" },
        { label: "Length:", value: toTitleCase(chatSettings?.length) || "Default" },
        { label: "Narration Style:", value: toTitleCase(chatSettings?.narrationStyle) || "Default" },
        { label: "Point of View:", value: toTitleCase(chatSettings?.pointOfView) || "Default" }
      ]
    },
    {
      icon: SparklesIcon,
      title: "A.I Settings",
      onClick: () => {
        createModal({
          content: <APISettingsModal chatID={chatID} />
        });
      },
      items: [
        { label: "Provider:", value: apiSettings?.provider },
        {
          label: "Model:",
          value: apiSettings?.model ?? "default"
        }
      ]
    },
    {
      icon: EllipsisHorizontalCircleIcon,
      title: "Misc",
      items: [
        {
          label: "Cosmetics",
          value: "",
          onClick: () => {
            createModal({ content: <CosmeticSettingsModal /> });
          }
        },
        {
          label: "Group Chats",
          value: "",
          onClick: () => {
            createModal({ content: <ManageCharactersModal chatID={chatID} /> });
          }
        }
      ]
    }
  ];

  const MenuItem = (item: Item) => {
    const Comp = item.closeOnClick !== false ? SheetClose : Fragment;
    return (
      <SidebarMenuItem key={item.label} className={"cursor-pointer"}>
        <SidebarMenuButton asChild onClick={item.onClick}>
          <Comp>
            <span className="text-tx-tertiary mr-5 select-none font-semibold">{item.label}</span>
            <p className="ml-auto line-clamp-1 select-none text-ellipsis font-medium">{item.value}</p>
          </Comp>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar className="" variant="overlay" side="right" {...rest}>
      <SidebarContent>
        {sidebarSpec.map((group) => (
          <SidebarGroup key={group.title} className="">
            <SidebarGroupLabel className="select-none gap-2">
              <group.icon className="text-tx-primary size-10" />
              <span className="text-tx-primary text-base font-bold">{group.title}</span>
            </SidebarGroupLabel>
            {/* Group Bento Container */}
            <div
              onClick={group.onClick}
              className="border-sidebar-border bg-sidebar-group-bg flex flex-col gap-2 rounded-2xl p-4 shadow-lg backdrop-blur-lg"
            >
              <SidebarMenu className="gap-2">{group.items.map((item) => MenuItem(item))}</SidebarMenu>
            </div>
          </SidebarGroup>
        ))}
      </SidebarContent>
    </Sidebar>
  );
}
